import React, { useState, useEffect, useMemo } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Sticky from 'react-stickynode';
import axios from 'axios';
import DropDown from 'components/elements/molecules/DropDown/DropDown';
import RadioGroup from 'components/elements/molecules/Radio/RadioGroup';
import RadioOption from 'components/elements/molecules/Radio/RadioOption';

import { bindActionCreators } from 'redux';
import * as Actions from 'redux/actions';
import { Icon, ICONS } from 'components/Icons';
import { getLogo, logoProvider } from 'helpers/sourceIcon';
import ActivityBreakdown from './components/ActivityBreakdown';
import ToolTip from 'components/elements/atoms/ToolTip/ToolTip.js';
import SourceDefault from 'helpers/source-default.svg';
import DollarIcon from 'assets/images/Dollar.svg';
import CommunityIcon from 'assets/images/Community.png';

import QuizQuestions from './components/QuizQuestions';

import './ChannelPage.scss';
import moment from 'moment';
import { replaceGoalText, handleEvent } from 'helpers';
import ChannelPageQuiz from './components/ChannelPageQuiz';
import { Dropdown, MenuItem } from 'react-bootstrap';

const sourceLogo = (url) => {
  try {
    const sourceURL = new URL(url);
    return getLogo({ domain: sourceURL.host });
  } catch (error) {
    console.log('Error while parsing url', url, error);
    return SourceDefault;
  }
};

const topSourceLogo = (source) => {
  if (source.sourceLogoURL) {
    return source.sourceLogoURL;
  }

  if (Array.isArray(source.domains) && source.domains.length > 0) {
    return getLogo({ domain: source.domains[0] });
  }

  return '';
};

function ChannelPage({
  mediaChannel = {},
  actions,
  mediaLogs,
  levelGoals,
  user,
  openBottomSheet,
  closeBottomSheet,
  goalsDefinition = [],
  openFlagArticle,
  openDeleteArticle,
  openReclassifyArticle,
  focusPointId,
  showChannelPageQuiz,
}) {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [filters, setFilters] = useState({
    subscribed: 'all',
    readArticleStyle: 'grayed-out',
  });
  const [expandFilters, setExpandFilters] = useState(false);
  const [showActivityBreakdown, setShowActivityBreakdown] = useState(false);
  const infoContainer = React.createRef();
  const subscriptionInfoContainer = React.createRef();

  const userLevel = useMemo(() => {
    try {
      const level = user.profile.roles.find(
        (r) => r.type === 'community',
      ).level;

      if (isNaN(level)) {
        return 1;
      }

      return level;
    } catch (error) {
      return 1;
    }
  }, [user]);

  const userLevelGoals = useMemo(() => {
    const descriptionMap = {};

    goalsDefinition.forEach((goal) => {
      descriptionMap[goal.goal] = goal.goalDisplayAs;
    });

    const userLevelGoals = levelGoals || {};

    const thisWeekGoals = Object.keys(userLevelGoals).map((levelGoal) => {
      const userGoal = userLevelGoals[levelGoal];
      let goalDisplayAs = descriptionMap[levelGoal];

      if (levelGoal.includes('LIST')) {
        const itemsCovered = Array.isArray(userGoal.itemsCovered)
          ? userGoal.itemsCovered
          : [];
        const itemsMissing = Array.isArray(userGoal.itemsMissing)
          ? userGoal.itemsMissing
          : [];

        goalDisplayAs = goalDisplayAs.replace(
          '{{VALUE}}',
          [...itemsCovered, ...itemsMissing].join(', '),
        );
      }

      return {
        status: userLevelGoals[levelGoal].status || false,
        goalDisplayAs:
          userGoal.goalDisplayAs ||
          replaceGoalText(goalDisplayAs, userGoal.target),
      };
    });

    const completedGoals = thisWeekGoals.filter((g) => g.status);
    const nonCompletedGoals = thisWeekGoals.filter((g) => !g.status);

    const percentageCompleted =
      (completedGoals.length /
        (completedGoals.length + nonCompletedGoals.length)) *
      100;

    return {
      completedGoals,
      nonCompletedGoals,
      percentageCompleted,
    };
  }, [goalsDefinition, levelGoals]);

  useEffect(() => {
    loadData();
  }, []);

  const isEmptyUser = useMemo(() => {
    return typeof user === 'object' && Object.keys(user).length === 0;
  }, [user]);

  const isCurator = useMemo(() => {
    return !isEmptyUser
      ? user.profile.roles.findIndex(
          (i) => i.type === 'curator' && i.status === 'activated',
        ) !== -1
      : false;
  }, [user]);

  async function loadData() {
    try {
      const promiseArray = [];
      promiseArray.push(loadMediaChannel());
      promiseArray.push(loadMediaLogsAndWeeklyGoals());
      await Promise.all(promiseArray);

      actions.getUserTopSources();
      actions.getSubscribedSources();

      handleEvent('CHANNEL_PAGE_VIEWED', {
        user,
      });
    } finally {
      setLoading(false);
    }
  }

  async function loadMediaChannel() {
    if (!mediaChannel || !Object.keys(mediaChannel).length) {
      return actions.getMediaChannel();
    }
  }

  function loadMediaLogsAndWeeklyGoals() {
    if (
      !isEmptyUser &&
      (!mediaLogs ||
        typeof mediaLogs !== 'object' ||
        Object.keys(mediaLogs).length === 0)
    ) {
      return actions.getUserPerformanceForCurrentWeek(user.username);
    }
  }

  const subscriptionText = useMemo(() => {
    let text = '';

    const subscribedSources =
      (mediaChannel && mediaChannel.subscribedSources) || [];

    if (subscribedSources.length) {
      const textArray = [];
      text = 'Currently, you have subscribed to ';
      for (const source of subscribedSources.slice(0, 2)) {
        textArray.push(`<b>${source}</b>`);
      }

      text += textArray.join(', ');

      if (subscribedSources.length > 2) {
        text += ` & ${subscribedSources.length - 2} more`;
      }

      text += '. ';
    }

    return text;
  }, [mediaChannel]);

  const quizExpandedEvent = (pointId) => {
    actions.quizExpandedEvent(pointId);
  };

  const logMedia = (sourceUrl) => {
    if (!isEmptyUser) {
      actions
        .logMedia(sourceUrl, user.username, 'Channel Page')
        .then(() => {
          console.log('Successfully logged media');
        })
        .catch((error) => {
          console.error('Error while logging media', error);
        });
    }
    window.open(sourceUrl, '_blank').focus();
  };

  mediaChannel = mediaChannel || {};
  const topStories = Array.isArray(mediaChannel.topStories)
    ? mediaChannel.topStories
    : [];
  // const communityPickArticles = Array.isArray(
  //   mediaChannel.communityPickArticles,
  // )
  //   ? mediaChannel.communityPickArticles
  //   : [];
  // const weeklyGoalArticles = Array.isArray(mediaChannel.weeklyGoalArticles)
  //   ? mediaChannel.weeklyGoalArticles
  //   : [];
  const articlesOfDay = Array.isArray(mediaChannel.articlesOfDay)
    ? mediaChannel.articlesOfDay
    : [];
  // const goDeeper =
  //   (mediaChannel.goDeeper && Object.keys(mediaChannel.goDeeper)) || [];
  // const goWider =
  //   (mediaChannel.goWider && Object.keys(mediaChannel.goWider)) || [];

  const topSuggestedSources = mediaChannel.topSuggestedSources || {};
  const localSources = Array.isArray(topSuggestedSources.local)
    ? topSuggestedSources.local
    : [];
  const nationalSources = Array.isArray(topSuggestedSources.national)
    ? topSuggestedSources.national
    : [];
  const regionalSources = Array.isArray(topSuggestedSources.regional)
    ? topSuggestedSources.regional
    : [];

  let defaultShowTopStories = 3;

  while (defaultShowTopStories < 10) {
    if (
      !topStories[defaultShowTopStories] ||
      !topStories[defaultShowTopStories].isCommunityPick ||
      topStories[defaultShowTopStories].score < 10
    ) {
      break;
    }

    defaultShowTopStories++;
  }

  useEffect(() => {
    async function onScroll(e) {
      if (loadingMore) {
        return;
      }

      if (
        e.target.scrollTop + e.target.offsetHeight + 1000 >
        e.target.scrollHeight
      ) {
        setLoadingMore(true);
        actions
          .getMediaChannel(Math.floor(topStories.length / 20) + 1)
          .then(() => {
            setLoadingMore(false);
          });
      }
    }

    document
      .querySelector('.middle-container')
      .addEventListener('scroll', onScroll);

    return () => {
      const middleContainer = document.querySelector('.middle-container');

      if (middleContainer) {
        middleContainer.removeEventListener('scroll', onScroll);
      }
    };
  }, [topStories, loadingMore]);

  // const isTopArticlesLatest = useMemo(() => {
  //   if (communityPickArticles.length === 0) {
  //     return true;
  //   }

  //   if (topStories.length === 0) {
  //     return false;
  //   }

  //   return moment(topStories[0].sourceDate).isAfter(communityPickArticles[0].createdAt);
  // }, [topStories, communityPickArticles]);

  // function renderTopicProgress(topic) {
  //   let message = '';
  //   let articlesLeft = 3;

  //   if (!isEmptyUser) {
  //     const topicInfo =
  //       mediaLogs &&
  //       mediaLogs.topics &&
  //       mediaLogs.topics.find((t) => t.name === topic);
  //     const topicCount = topicInfo ? topicInfo.number : 0;
  //     articlesLeft -= topicCount;
  //     if (articlesLeft < 0) articlesLeft = 0;
  //     switch (articlesLeft) {
  //       case 3:
  //         message = `You haven't read ${topic} articles this week.`;
  //         break;
  //       case 0:
  //         message = `You've read ${topicCount} articles on ${topic} this week. Nice work!`;
  //         break;
  //       default:
  //         // eslint-disable-next-line no-case-declarations
  //         const articleText = topicCount === 1 ? 'article' : 'articles';
  //         message = `You've read ${topicCount} ${topic} ${articleText} this week. Read ${articlesLeft} more to meet your weekly goal.`;
  //     }
  //   } else {
  //     message =
  //       'Sign in to track your reading progress across important issues, and read a healthy mix of sources, topics, and location.';
  //     articlesLeft = 2;
  //   }

  //   return (
  //     <ToolTip toolTipText={message} toolTipPosition="top">
  //       <div className="topic-progress">
  //         <div
  //           className="completed"
  //           style={{ width: `${((3 - articlesLeft) * 100) / 3}%` }}
  //         />
  //       </div>
  //     </ToolTip>
  //   );
  // }

  function renderSource(source) {
    return (
      <div
        className="d-flex align-items-center mt-3 topic"
        key={source._id}
        onClick={() => {
          logMedia(`https://${source.domains[0]}`);
        }}
      >
        <img src={topSourceLogo(source)} />
        {source.sourceName}
      </div>
    );
  }

  function renderNationalSources() {
    if (!nationalSources.length) {
      return null;
    }

    const sources = mediaLogs && mediaLogs.sources ? mediaLogs.sources : [];

    let sorted;

    if (sources.length) {
      sorted = nationalSources.sort((a, b) => {
        const aSource = mediaLogs.sources.find((s) => s.name === a.sourceName);
        const bSource = mediaLogs.sources.find((s) => s.name === b.sourceName);
        const aFrequency = aSource ? aSource.number : 0;
        const bFrequency = bSource ? bSource.number : 0;
        return aFrequency < bFrequency ? -1 : 1;
      });
    } else {
      sorted = nationalSources;
    }

    return (
      <div className="mt-4">
        <div className="title">National</div>
        <div className="topics">{sorted.slice(0, 5).map(renderSource)}</div>
      </div>
    );
  }

  function renderRegionalSources() {
    if (!regionalSources.length) {
      return null;
    }

    return (
      <div className="mt-4">
        <div className="title">Regional</div>
        <div className="topics">
          {regionalSources.slice(0, 5).map(renderSource)}
        </div>
      </div>
    );
  }

  function renderLocalSources() {
    if (!localSources.length) {
      return null;
    }

    return (
      <div className="mt-4">
        <div className="title">Local</div>
        <div className="topics">
          {localSources.slice(0, 5).map(renderSource)}
        </div>
      </div>
    );
  }

  function hintForSources() {
    if (!isEmptyUser) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="mt-4">
          We pick articles for you from over 1,800 American and 70 international
          newspapers to broaden and deepen your knowledge about important
          issues.
        </div>
        <div className="mt-3">
          <Link to="/auth/signUp" className="link-blue">
            Sign up
          </Link>{' '}
          for your recommended articles.
        </div>
      </React.Fragment>
    );
  }

  function renderTopSources() {
    return (
      <div
        className={`card py-5 px-4 ${
          !isEmptyUser ? 'mt-5' : 'card-landing-page'
        }`}
      >
        <h3>Top Sources</h3>
        {renderNationalSources()}
        {renderRegionalSources()}
        {renderLocalSources()}
        {hintForSources()}
      </div>
    );
  }

  function renderLink(topic) {
    const newsClubLink = isEmptyUser
      ? '/newsClub'
      : `/portfolio/${user.username}/club`;

    return (
      <div className="mt-3 topic" key={topic}>
        <Link to={newsClubLink} className="link-blue clubhouse-link">
          # {topic}
        </Link>
      </div>
    );
  }

  function renderTopicLink(topic) {
    return (
      <div
        className="mt-3 topic"
        key={topic}
        onClick={() => {
          const section = document.getElementById(`${topic}-section`);

          if (!section) {
            return;
          }

          document.scrollingElement.scroll({
            top:
              section.getBoundingClientRect().y -
              document.scrollingElement.getBoundingClientRect().y -
              100,
            behavior: 'smooth',
          });
        }}
      >
        # {topic}
      </div>
    );
  }

  // function renderGoDeeperTopics() {
  //   if (!goDeeper.length) {
  //     return null;
  //   }

  //   return (
  //     <div>
  //       <div className="title">Go deeper:</div>
  //       <div className="topics">
  //         {goDeeper.map((topic) => renderTopicLink(topic))}
  //       </div>
  //     </div>
  //   );
  // }

  // function renderExploreNewTopics() {
  //   if (!goWider.length) {
  //     return null;
  //   }

  //   return (
  //     <div className={goDeeper.length !== 0 ? 'mt-4' : ''}>
  //       <div className="title">Explore new topics:</div>
  //       <div className="topics">
  //         {goWider.map((topic) => renderTopicLink(topic))}
  //       </div>
  //     </div>
  //   );
  // }

  const BottomSheetContent = () => (
    <ActivityBreakdown
      showByDefault={true}
      userLevelGoals={userLevelGoals}
      closeBottomSheet={closeBottomSheet}
      isEmptyUser={isEmptyUser}
      userLevel={userLevel}
    />
  );

  function renderFloatingButton() {
    return (
      <div
        className="floating-button"
        onClick={() => openBottomSheet({ child: BottomSheetContent })}
      >
        {userLevel}
      </div>
    );
  }

  function renderTopicSection(forMobile = false) {
    const topicSection = (
      <div className="topic-section">
        {!isEmptyUser ? (
          <React.Fragment>
            <div className="card blue-card py-3 px-4 mb-5 desktop-only">
              <ActivityBreakdown
                showByDefault={false}
                showActivityBreakdown={showActivityBreakdown}
                setShowActivityBreakdown={setShowActivityBreakdown}
                userLevelGoals={userLevelGoals}
                isEmptyUser={isEmptyUser}
                userLevel={userLevel}
              />
            </div>
            <div className="card py-3 px-4">
              <div>
                <div className="topics mb-3">
                  {renderLink('NewsClubReadings')}
                </div>
              </div>
              <div>
                <div className="topics mb-3">
                  {renderTopicLink('Community Picks')}
                </div>
              </div>
              {/* {renderGoDeeperTopics()} */}
              {/* {renderExploreNewTopics()} */}
            </div>
          </React.Fragment>
        ) : (
          <div className="card blue-card py-3 px-4 mb-5 desktop-only">
            <ActivityBreakdown
              showByDefault={false}
              showActivityBreakdown={showActivityBreakdown}
              setShowActivityBreakdown={setShowActivityBreakdown}
              userLevelGoals={userLevelGoals}
              isEmptyUser={isEmptyUser}
              userLevel={userLevel}
            />
          </div>
        )}
        {renderTopSources()}
      </div>
    );

    if (forMobile) {
      return topicSection;
    }

    return (
      <Sticky top={0} innerZ="999" className="">
        {topicSection}
      </Sticky>
    );
  }

  const getPopoverContent = () => {
    return `<div>
    Every week, AverPoint hosts a deep-dive on an important issue. We'll send you 5 articles picked by an expert, and feature the expert on a Clubhouse talk. View our past talks <a href="/newsClub" class="link-blue">here</a>.
    </div>`;
  };

  const showHelperMessage = (e) => {
    e.stopPropagation();
    actions.showPopover(
      infoContainer,
      'top',
      ICONS.INFO,
      'Weekly Reading',
      getPopoverContent(),
      null,
      null,
      [],
      true,
    );
  };

  const getPopoverSubscriptionContent = () => {
    return `<div>
    We'll only show articles from free sources and paid sources, if you have a subscription.
    ${subscriptionText}${
      isEmptyUser
        ? 'To update your sources please <a href="/auth/login" class="link-blue">login</a>'
        : `Update your sources <a href="/portfolio/${user.username}/profile" class="link-blue">here</a>`
    }.
    </div>`;
  };

  const showSubscriptionMessage = (e) => {
    e.stopPropagation();
    actions.showPopover(
      subscriptionInfoContainer,
      'top',
      ICONS.INFO,
      'Available to read',
      getPopoverSubscriptionContent(),
      null,
      null,
      [],
      true,
    );
  };

  const showRadioFilter = (id, options) => {
    return (
      <RadioGroup
        name={id}
        handleChange={(event) => {
          setFilters((prev) => ({
            ...prev,
            [id]: event.target.value,
          }));
        }}
      >
        <div className="d-flex filter-group">
          {options.map((o, i) => (
            <div className="d-flex align-items-center">
              <RadioOption
                id={`${id}-${i}`}
                name={id}
                value={o.value}
                label={o.label}
                checked={filters[id] === o.value}
              />
              {o.showHint && (
                <div
                  ref={subscriptionInfoContainer}
                  className="subscription-info ml-2"
                >
                  <Icon
                    icon={ICONS.INFO}
                    size={20}
                    className="av-blue"
                    onClick={showSubscriptionMessage}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </RadioGroup>
    );
  };

  // const showCommunityPickArticles = () => {
  //   return (
  //     communityPickArticles.length > 0 && (
  //       <React.Fragment>
  //         <div className="section-title" id="Community Picks-section">
  //           <div className="d-flex topic">
  //             <div className="topic-hash">#</div>
  //             <div className="ml-3 top-stories-title">
  //               <div className="topic-name">Community Picks</div>
  //             </div>
  //           </div>
  //         </div>
  //         <ArticleListByTopic
  //           articles={communityPickArticles}
  //           logMedia={logMedia}
  //           type="Community Picks"
  //           quizExpandedEvent={quizExpandedEvent}
  //           isCommunityPick
  //           actions={actions}
  //           isCurator={isCurator}
  //           isEmptyUser={isEmptyUser}
  //           openFlagArticle={openFlagArticle}
  //           openDeleteArticle={openDeleteArticle}
  //           openReclassifyArticle={openReclassifyArticle}
  //           focusPointId={focusPointId}
  //           filters={filters}
  //         />
  //       </React.Fragment>
  //     )
  //   );
  // };

  const showTopArticles = () => {
    return (
      topStories.length > 0 && (
        <React.Fragment>
          <div className="section-title align-items-center">
            <div className="d-flex topic">
              <div className="topic-hash">#</div>
              <div className="ml-3 top-stories-title">
                <div className="topic-name">Top Stories</div>
              </div>
            </div>
            <Link to="/help-page?helpId=66eb5856ef6401048f53549f">
              <div
                className="av-blue cursor-pointer"
                onClick={() => {
                  handleEvent('INFO_VIEWED_SOURCE_APPROVAL', {
                    client: 'Web app',
                    page: 'Channel Page',
                  });
                }}
              >
                How do we approve sources?
              </div>
            </Link>
            {/* <Link to="/blog?blogId=6286824372d7da3df55b2b65">
            <div className="d-flex align-items-center link-algorithm">
              <Icon
                icon={ICONS.FILTER}
                size={20}
                className="av-blue mr-2"
              />
              <div className="the-algorithm">The Algorithm</div>
            </div>
          </Link> */}
          </div>
          <ArticleListByTopic
            articles={topStories}
            logMedia={logMedia}
            type="Top Stories"
            quizExpandedEvent={quizExpandedEvent}
            actions={actions}
            isCurator={isCurator}
            isEmptyUser={isEmptyUser}
            openFlagArticle={openFlagArticle}
            openDeleteArticle={openDeleteArticle}
            openReclassifyArticle={openReclassifyArticle}
            focusPointId={focusPointId}
            filters={filters}
            defaultShowArticles={defaultShowTopStories}
          />
        </React.Fragment>
      )
    );
  };

  if (loading) {
    return (
      <div className="text-center mt-4">
        <div
          className="fa fa-spin fa-spinner text-primary"
          style={{ fontSize: '40px' }}
        />
      </div>
    );
  }

  if (showChannelPageQuiz) {
    return (
      <div className="channel-page">
        <ChannelPageQuiz
          articleId={showChannelPageQuiz}
          returnToChannelPage={() => actions.toggleChannelPageQuiz(false)}
        />
      </div>
    );
  }

  return (
    <div className="channel-page">
      <div className="sources-container mobile-only">
        <div
          className="title p-4 d-flex align-items-center"
          onClick={() => setShowSources(!showSources)}
        >
          <Icon
            icon={showSources ? ICONS.CHEVRON_DOWN : ICONS.CHEVRON_RIGHT}
            className="mr-1"
          />{' '}
          Sources
        </div>
        {showSources && <div className="p-4">{renderTopicSection(true)}</div>}
      </div>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-9">
            <div className="section">
              {/* <div className="yellow-card p-4">
                <img src="https://res.cloudinary.com/averpoint/image/upload/v1655635058/Icons/mic.png" />
                <div>
                  This week, AverPoint's News Club is hosting Yasha Mounk who
                  will lead a discussion on Diverse Democracies: Why they Fall
                  Apart and How they can Endure.{' '}
                  <Link to="/newsClub" className="link-blue">
                    Click here
                  </Link>{' '}
                  for the pre-readings and more info.
                </div>
              </div> */}
              <div className="filter-container">
                <div className="filter-header d-flex align-items-center justify-content-end">
                  <div
                    className="d-flex align-items-center filter-title-container"
                    onClick={() => setExpandFilters((prev) => !prev)}
                  >
                    <Icon
                      icon={ICONS.FILTER}
                      size={20}
                      className="av-blue mr-2"
                    />
                    <span className="filter-title">Filters</span>
                  </div>
                  <Link to="/blog?blogId=6286824372d7da3df55b2b65">
                    <div
                      className="d-flex align-items-center link-algorithm ml-4"
                      onClick={() => {
                        handleEvent('ALGORITHM_VIEWED', {});
                      }}
                    >
                      <Icon
                        icon={ICONS.ALGORITHM}
                        size={20}
                        className="av-blue mr-2"
                      />
                      <div className="filter-title">The Algorithm</div>
                    </div>
                  </Link>
                </div>
                {expandFilters && (
                  <div className="filter-body">
                    <div className="av-blue">
                      Should we include articles that require subscription?
                    </div>
                    {showRadioFilter(
                      'subscribed',
                      [
                        { label: 'Yes, show everything', value: 'all' },
                        {
                          label: 'Only articles I can access',
                          value: 'available',
                          showHint: true,
                        },
                      ],
                      true,
                    )}
                    {!isEmptyUser && (
                      <React.Fragment>
                        <div className="av-blue mt-3">
                          How should we show articles you've read?
                        </div>
                        {showRadioFilter('readArticleStyle', [
                          { label: 'Grayed out', value: 'grayed-out' },
                          { label: 'No change', value: 'no-change' },
                          { label: 'Hide', value: 'hidden' },
                        ])}
                      </React.Fragment>
                    )}
                  </div>
                )}
              </div>
              <div className="section-container p-4">
                {articlesOfDay.length > 0 && (
                  <React.Fragment>
                    <div className="section-title mb-3">
                      <div className="d-flex topic">
                        <div className="topic-hash">#</div>
                        <div className="ml-3 top-stories-title">
                          <div className="topic-name">Article of the day</div>
                        </div>
                      </div>
                    </div>
                    <ArticleListByTopic
                      articles={articlesOfDay}
                      logMedia={logMedia}
                      type="Article Of Day"
                      isArticleOfDay
                      quizExpandedEvent={quizExpandedEvent}
                      filters={filters}
                      actions={actions}
                    />
                  </React.Fragment>
                )}
                {/* {weeklyGoalArticles.length > 0 && (
                  <div className="yellow-card">
                    <div className="section-title mb-0">
                      <div className="d-flex topic">
                        <div className="topic-hash">#</div>
                        <div className="ml-3 d-flex align-items-center">
                          <div>
                            <div className="topic-name">News Club Readings</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      ref={infoContainer}
                      className="weekly-goal-description d-flex align-items-center"
                    >
                      {mediaChannel.subject}
                      <Icon
                        icon={ICONS.INFO}
                        size={18}
                        className="ml-2"
                        onClick={showHelperMessage}
                      />
                    </div>
                    {mediaChannel.ctaLink && mediaChannel.ctaText && (
                      <div className="weekly-description mb-4">
                        {mediaChannel.ctaText}
                        <a
                          href={mediaChannel.ctaLink}
                          className="ml-2 av-blue d-inline-flex align-items-center"
                          target="_blank"
                        >
                          Join Here{' '}
                          <Icon icon={ICONS.NEW_TAB} className="ml-1" />
                        </a>
                      </div>
                    )}
                    <ArticleListByTopic
                      articles={weeklyGoalArticles}
                      logMedia={logMedia}
                      type="Weekly Readings"
                      quizExpandedEvent={quizExpandedEvent}
                      defaultShowArticles={5}
                      filters={filters}
                      actions={actions}
                    />
                  </div>
                )} */}
                {/* {isTopArticlesLatest ? (
                  <React.Fragment>
                    {showTopArticles()}
                    {showCommunityPickArticles()}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {showCommunityPickArticles()}
                    {showTopArticles()}
                  </React.Fragment>
                )} */}
                {showTopArticles()}
                {/* {goWider.map((topic) => (
                  <React.Fragment key={topic}>
                    <div className="section-title" id={`${topic}-section`}>
                      <div className="d-flex topic">
                        <div className="topic-hash">#</div>
                        <div className="ml-3">
                          <div className="topic-name">{topic}</div>
                          <div className="helper-message">
                            Continue reading about {topic.toLowerCase()} to
                            reach your weekly goals
                          </div>
                        </div>
                      </div>
                      {renderTopicProgress(topic)}
                    </div>
                    <ArticleListByTopic
                      articles={mediaChannel.goWider[topic]}
                      logMedia={logMedia}
                      topic={topic}
                      quizExpandedEvent={quizExpandedEvent}
                      actions={actions}
                      isCurator={isCurator}
                      isEmptyUser={isEmptyUser}
                      openFlagArticle={openFlagArticle}
                      openDeleteArticle={openDeleteArticle}
                      openReclassifyArticle={openReclassifyArticle}
                      focusPointId={focusPointId}
                      filters={filters}
                    />
                  </React.Fragment>
                ))}
                {goDeeper.map((topic) => (
                  <React.Fragment key={topic}>
                    <div className="section-title" id={`${topic}-section`}>
                      <div className="d-flex topic">
                        <div className="topic-hash">#</div>
                        <div className="ml-3">
                          <div className="topic-name">{topic}</div>
                          <div className="helper-message">
                            Continue reading about {topic.toLowerCase()} to
                            reach your weekly goals
                          </div>
                        </div>
                      </div>
                      {renderTopicProgress(topic)}
                    </div>
                    <ArticleListByTopic
                      articles={mediaChannel.goDeeper[topic]}
                      logMedia={logMedia}
                      topic={topic}
                      quizExpandedEvent={quizExpandedEvent}
                      actions={actions}
                      isCurator={isCurator}
                      isEmptyUser={isEmptyUser}
                      openFlagArticle={openFlagArticle}
                      openDeleteArticle={openDeleteArticle}
                      openReclassifyArticle={openReclassifyArticle}
                      focusPointId={focusPointId}
                      filters={filters}
                    />
                  </React.Fragment>
                ))} */}
              </div>
            </div>
          </div>
          {/* <div className="col-md-3 desktop-only">{renderTopicSection()}</div> */}
        </div>
      </div>
      {renderFloatingButton()}
    </div>
  );
}

ChannelPage.propTypes = {};

function relativeTime(time) {
  const dateToConvert = new Date(time).getTime();
  const now = new Date().getTime();

  const msPerMinute = 60 * 1000;
  const msPerHour = msPerMinute * 60;
  const msPerDay = msPerHour * 24;
  const msPerWeek = msPerDay * 7;

  const elapsed = now - dateToConvert;

  if (elapsed < 0) {
    return '';
  }

  if (elapsed < msPerDay) {
    return 'Posted today';
  }
  if (elapsed < msPerWeek) {
    return `${Math.round(elapsed / msPerDay)} days ago`;
  }
  return `Posted on ${moment(time).format('MM-DD-YYYY')}`;
}

function mapStateToProps(state, ownProps) {
  let focusPointId = null;

  try {
    const searchParams = new URLSearchParams(ownProps.location.search);

    focusPointId = searchParams.get('pointId') || null;
  } catch (error) {
    console.error('Error while getting query param');
  }

  return {
    mediaChannel: state.mediaLogs.mediaChannel,
    mediaLogs: state.mediaLogs.mediaLogs,
    levelGoals: state.weeklyGoals.levelGoals,
    goalsDefinition: state.weeklyGoals.goalsDefinition,
    showChannelPageQuiz: state.mediaLogs.showChannelPageQuiz,
    user: state.user.user,
    focusPointId,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
    openBottomSheet: (props = {}) => {
      dispatch(Actions.openBottomSheet(props));
    },
    closeBottomSheet: (props = {}) => {
      dispatch(Actions.hideBottomSheet(props));
    },
    openFlagArticle: (pointId) => {
      const props = { pointId };
      dispatch(Actions.openModal('flag-article', props));
    },
    openDeleteArticle: (pointId) => {
      const props = { pointId };
      dispatch(Actions.openModal('delete-article', props));
    },
    openReclassifyArticle: (pointId, isAdmin, pointSourceAnalytics) => {
      const props = { pointId, isAdmin, pointSourceAnalytics };
      dispatch(Actions.openModal('reclassify-article', props));
    },
  };
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ChannelPage),
);

export function ArticleListByTopic({
  articles,
  logMedia,
  topic = '',
  type = null,
  isArticleOfDay = false,
  quizExpandedEvent,
  isCommunityPick = false,
  actions,
  isCurator = false,
  isEmptyUser = true,
  openFlagArticle = null,
  openDeleteArticle = null,
  openReclassifyArticle = null,
  defaultShowArticles = 3,
  focusPointId = null,
  filters = {},
  setShowQuizPage = () => {},
}) {
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (focusPointId) {
      const foundArticle = articles.find((a) => a._id === focusPointId);

      if (foundArticle) {
        setShowMore(true);

        setTimeout(() => {
          const section = document.getElementById(focusPointId);

          if (section) {
            document.scrollingElement.scroll({
              top:
                section.getBoundingClientRect().y -
                document.scrollingElement.getBoundingClientRect().y -
                (isEmptyUser ? 10 : 100),
              behavior: 'smooth',
            });
          }
        }, 1000);
      }
    }
  }, []);

  const filteredArticles = useMemo(() => {
    return articles
      .filter(
        (a) =>
          filters.subscribed === 'all' ||
          a.paywallStatus === 'free' ||
          a.isSubscribed,
      )
      .filter(
        (a) =>
          ['grayed-out', 'no-change'].includes(filters.readArticleStyle) ||
          !a.isRead,
      );
  }, [filters, articles]);

  const isShowMore = useMemo(() => {
    return articles.length > defaultShowArticles;
  }, [filteredArticles]);

  const filteredArticlesList = useMemo(() => {
    if (showMore) {
      return filteredArticles;
    }

    // return filteredArticles.slice(0, defaultShowArticles);
    return filteredArticles;
  }, [showMore, filteredArticles]);

  function renderMediaCard(mediaCollection, index) {
    return (
      <MediaCollectionCard
        mediaCollection={mediaCollection}
        key={mediaCollection._id}
        logMedia={logMedia}
        isArticleOfDay={isArticleOfDay}
        quizExpandedEvent={quizExpandedEvent}
        isCommunityPick={isCommunityPick}
        actions={actions}
        isCurator={isCurator}
        isEmptyUser={isEmptyUser}
        openFlagArticle={openFlagArticle}
        openDeleteArticle={openDeleteArticle}
        openReclassifyArticle={openReclassifyArticle}
        filters={filters}
        index={index}
      />
    );
  }

  // const topicText = type || `On ${topic}`;

  return (
    <div>
      {filteredArticlesList.map(renderMediaCard)}
      {/* {isShowMore && (
        <div
          className="av-blue my-3 d-flex align-items-center"
          onClick={() => setShowMore(!showMore)}
          style={{ cursor: 'pointer' }}
        >
          <Icon
            icon={showMore ? ICONS.EXPAND_LESS : ICONS.EXPAND_MORE}
            size={24}
            className="mr-2"
          />
          {showMore ? `Show Less ${topicText}` : `More ${topicText}`}
        </div>
      )} */}
    </div>
  );
}

function MediaCollectionCard({
  mediaCollection,
  logMedia,
  isArticleOfDay,
  quizExpandedEvent,
  isCommunityPick = false,
  actions,
  isCurator = false,
  isEmptyUser = true,
  openFlagArticle = null,
  openDeleteArticle = null,
  openReclassifyArticle = null,
  filters = {},
  index,
}) {
  const [quizExpanded, setQuizExpanded] = useState(false);
  const [eventFired, setEventFired] = useState(false);
  const infoContainer = React.createRef();
  const scoreContainer = React.createRef();

  const sourceAnalytics = mediaCollection.sourceAnalytics || {};
  const topic = sourceAnalytics.topic || '';
  const topLevelTopics = Array.isArray(sourceAnalytics.topLevelTopics)
    ? sourceAnalytics.topLevelTopics
    : [];
  const secondLevelTopic = Array.isArray(sourceAnalytics.secondLevelTopic)
    ? sourceAnalytics.secondLevelTopic
    : [];
  const thirdLevelTopic = Array.isArray(sourceAnalytics.thirdLevelTopic)
    ? sourceAnalytics.thirdLevelTopic
    : [];

  useEffect(() => {
    if (quizExpanded && !eventFired) {
      setEventFired(true);
      quizExpandedEvent(mediaCollection.pointId);
    }
  }, [quizExpanded]);

  useEffect(() => {
    if (index < 10) {
      actions
        .getQuizQuestionsForArticle(
          mediaCollection.pointId,
          null,
          null,
          true,
          true,
        )
        .then((response) => {});
    }
  }, []);

  const questionsExist =
    Array.isArray(mediaCollection.quizQuestions) &&
    mediaCollection.quizQuestions.length > 0;

  function renderQuestionsCount() {
    const className = classnames('d-inline-flex align-items-center', {
      zero: !(mediaCollection.numQTP || 0),
    });
    return (
      <div className={className}>
        <Icon icon={ICONS.QUESTION} />
        <div className="ml-2">Questions {mediaCollection.numQTP || 0}</div>
      </div>
    );
  }

  function renderReviewsCount() {
    const className = classnames('d-inline-flex align-items-center ml-4', {
      zero: !(mediaCollection.numRTP || 0),
    });
    return (
      <div className={className}>
        <Icon icon={ICONS.REVIEW} />
        <div className="ml-2">Reviews {mediaCollection.numRTP || 0}</div>
      </div>
    );
  }

  function renderQuizLink() {
    if (!questionsExist) {
      return null;
    }

    const isQuizNotSubmitted = mediaCollection.quizQuestions.some(
      (i) => !i.userAnswer || !i.userAnswer.isSubmitted,
    );

    const className = classnames('d-inline-flex align-items-center ml-4');
    return (
      <div className={className}>
        <div
          className="ml-2 text-underline av-blue cursor-pointer"
          style={{ zIndex: 200 }}
          onClick={() => actions.toggleChannelPageQuiz(mediaCollection.pointId)}
        >
          {isQuizNotSubmitted ? 'Quiz' : 'Quiz response'}
        </div>
      </div>
    );
  }

  function renderBottomSection() {
    return (
      <div className="bottom-section p-4">
        {renderQuestionsCount()}
        {renderReviewsCount()}
      </div>
    );
  }

  function showTopic(topicName, topic) {
    if (!Array.isArray(topic) || !topic.length) {
      return null;
    }
    return (
      <div className="d-inline-flex align-items-center ml-4">
        <div className="ml-2">
          {topicName}: <strong>{topic.join(', ') || '-'}</strong>
        </div>
      </div>
    );
  }

  function renderTopicSection() {
    if (!mediaCollection.isCommunityPick) {
      return null;
    }

    let topics = [];

    if (topic) {
      topics.push(topic);
    }

    topics = topics.concat([
      ...topLevelTopics,
      ...secondLevelTopic,
      ...thirdLevelTopic,
    ]);

    return (
      <React.Fragment>
        <div className="d-inline-flex align-items-center">
          <div className="ml-2">
            Topic{topics.length > 1 ? 's' : ''}:{' '}
            <strong>{topics.length > 0 ? topics.join(', ') : '-'}</strong>
          </div>
        </div>
      </React.Fragment>
    );
  }

  function renderScoreSection() {
    if (!mediaCollection.score) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="d-inline-flex align-items-center">
          <div
            ref={scoreContainer}
            className="ml-4 position-relative"
            onClick={showScoreHelperMessage}
          >
            Score:{' '}
            <span className="av-blue">
              <strong>{parseInt(mediaCollection.score)}</strong>
            </span>
          </div>
        </div>
      </React.Fragment>
    );
  }

  function renderFeedback(mediaCollection) {
    return (
      <Dropdown
        className="topic-feedback"
        id="topic-feedback"
        onClick={(e) => e.stopPropagation()}
        pullRight
      >
        <Dropdown.Toggle noCaret className="av-blue">
          Feedback
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <MenuItem disabled className="av-gray font-size-12">
            What feedback?
          </MenuItem>
          <MenuItem
            onClick={() => {
              openFlagArticle(mediaCollection.pointId);
            }}
          >
            Flag article
          </MenuItem>
          <MenuItem
            onClick={() => {
              openReclassifyArticle(mediaCollection.pointId, isCurator, {
                ...(mediaCollection.sourceAnalytics || {}),
                topic: topic ? [topic] : [],
              });
            }}
          >
            Suggest topics
          </MenuItem>
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  function renderTopicAndScoreSection() {
    return (
      <div className="bottom-section p-4 justify-content-between">
        <div className="d-flex align-items-center">
          {mediaCollection.isRead &&
            filters.readArticleStyle === 'grayed-out' && (
              <div
                className="grey-overlay"
                onClick={showScoreHelperMessage}
              ></div>
            )}
          {renderTopicSection()}
          {renderScoreSection()}
          {renderQuizLink()}
        </div>
        <div>{renderFeedback(mediaCollection)}</div>
      </div>
    );
  }

  // function renderQuestions() {
  //   if (!questionsExist) {
  //     return null;
  //   }

  //   return (
  //     <div className="bottom-section p-4 questions">
  //       {mediaCollection.isRead &&
  //         filters.readArticleStyle === 'grayed-out' && (
  //           <div className="grey-overlay"></div>
  //         )}
  //       <span
  //         className="av-blue question-header"
  //         onClick={() => setQuizExpanded(!quizExpanded)}
  //       >
  //         Quiz
  //         <Icon
  //           icon={quizExpanded ? ICONS.EXPAND_LESS : ICONS.EXPAND_MORE}
  //           size={18}
  //           className="ml-1"
  //         />
  //       </span>
  //       {quizExpanded &&
  //         mediaCollection.quizQuestions.map((question) => (
  //           <QuizQuestions question={question} />
  //         ))}
  //     </div>
  //   );
  // }

  function isAnonymousAuthor(author) {
    return (
      author.toLowerCase().includes('anonymous') ||
      author.toLowerCase() === 'by'
    );
  }

  const description = mediaCollection.sourceDescription
    ? `${mediaCollection.sourceDescription.substring(0, 200)}${
        mediaCollection.sourceDescription.length > 200 ? '...' : ''
      }`
    : '';

  const getPopoverContent = () => {
    return `<div>
      AverPoint's community members are reading this article. Click <a href="/blog?blogId=6286824372d7da3df55b2b65">here</a> to understand how we rank articles.
    </div>`;
  };

  const showHelperMessage = (e) => {
    e.stopPropagation();
    actions.showPopover(
      infoContainer,
      'top',
      ICONS.INFO,
      'Community Picks',
      getPopoverContent(),
      null,
      null,
      [],
      true,
    );
  };

  const showScoreHelperMessage = (e) => {
    handleEvent('INFO_VIEWED_NEWS_SCORE', {
      sourceURL: mediaCollection.sourceURL,
      amcId: mediaCollection.pointId,
      client: 'Web app',
      page: 'Channel Page',
    });

    e.stopPropagation();
    const position =
      scoreContainer.current.getBoundingClientRect().top +
        document.querySelector('.portfolio-container').scrollTop -
        80 >
      400
        ? 'top'
        : 'bottom';
    actions.showPopover(
      scoreContainer,
      position,
      ICONS.INFO,
      'Reading time breakdown by user level',
      getScorePopoverContent(mediaCollection),
      null,
      null,
      [],
      false,
    );
  };

  const menuItems = [
    ...(isCurator
      ? [
          {
            label: (
              <div className="d-flex align-items-center">
                <Icon
                  icon={ICONS.DELETE}
                  size={20}
                  className="flag-icon mr-2"
                />{' '}
                Remove
              </div>
            ),
            onClick: () => {
              openDeleteArticle(mediaCollection.pointId);
            },
          },
        ]
      : []),
  ];

  return (
    <div
      className="mb-4 card"
      key={mediaCollection.pointId}
      id={mediaCollection.pointId}
    >
      <div
        className="top-section p-4"
        onClick={() => logMedia(mediaCollection.sourceURL)}
      >
        {mediaCollection.isRead &&
          filters.readArticleStyle === 'grayed-out' && (
            <div className="grey-overlay"></div>
          )}
        <div className="article-main">
          <div className="source-name-title">
            <div className="source-name">
              <img src={sourceLogo(mediaCollection.sourceURL)} />
              {mediaCollection.sourceName}
              {mediaCollection.isAverPointReader && (
                <ToolTip
                  toolTipText="This article is on AverPoint Reader"
                  toolTipPosition="top"
                >
                  <img src={`${logoProvider}averpoint.com`} className="ml-2" />
                </ToolTip>
              )}
              {mediaCollection.paywallStatus !== 'free' && (
                <ToolTip
                  toolTipText={`${mediaCollection.sourceName} may require a subscription.`}
                  toolTipPosition="top"
                >
                  <img src={DollarIcon} className="ml-2" />
                </ToolTip>
              )}
              {mediaCollection.isCommunityPick && (
                <div ref={infoContainer} className="community-icon">
                  <img
                    src={CommunityIcon}
                    className="ml-2"
                    onClick={showHelperMessage}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="title my-2">
            <div>
              {mediaCollection.sourceTitle || mediaCollection.sourceURL}
            </div>
            <div className="description">{description}</div>
          </div>
          <div className="middle-section">
            {mediaCollection.points && (
              <span className="points">{mediaCollection.points} points</span>
            )}
            {mediaCollection.points && mediaCollection.sourceDate && (
              <Icon icon={ICONS.BULLET} className="icon mx-3" />
            )}
            {mediaCollection.sourceDate && (
              <span className="time">
                {relativeTime(mediaCollection.sourceDate)}
              </span>
            )}
          </div>
          {mediaCollection.sourceAuthor &&
            !isAnonymousAuthor(mediaCollection.sourceAuthor) && (
              <div className="author mt-2">{mediaCollection.sourceAuthor}</div>
            )}
        </div>
        <div className="article-image">
          {mediaCollection.sourceImageURL && (
            <img src={mediaCollection.sourceImageURL} />
          )}
        </div>
        {!isEmptyUser &&
          menuItems.length > 0 &&
          (mediaCollection.isCommunityPick ? (
            <div className="source-menu">
              <DropDown
                menuItems={menuItems}
                containerClassName={'more-info-container'}
                componentClassName={'more-info'}
                iconName={'MORE'}
                iconClassName={'channel-more-info-icon'}
                menuClassName={`channel-more-info-dropdown ${
                  isCurator ? 'full-dropdown' : ''
                }`}
              />
            </div>
          ) : (
            <div className="source-menu"></div>
          ))}
      </div>
      {mediaCollection.isCommunityPick ||
      mediaCollection.score ||
      questionsExist
        ? renderTopicAndScoreSection()
        : null}
      {mediaCollection.numQTP || mediaCollection.numRTP
        ? renderBottomSection()
        : null}
    </div>
  );
}

const getScorePopoverContent = (mediaCollection) => {
  const scoresByLevel = mediaCollection.scoresByLevel || {};

  const showReadTimeBreakdown = () => {
    if (!Object.keys(scoresByLevel).length) {
      return null;
    }

    return (
      <ul>
        {Object.keys(scoresByLevel).map((level) => {
          return (
            <li>
              Level {level}: <b>{scoresByLevel[level]} minutes</b>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div>
      {showReadTimeBreakdown()}
      <h4>
        <b>How do we score an article?</b>
      </h4>
      <div>
        An article's score reflects how many minutes it was read and the quality
        of its readers. When a user reads an article, the article's score goes
        up by the minutes read times the user's level. This reflects 2 of our
        values
      </div>
      <ul>
        <li>Reading time matters more than clicks.</li>
        <li>Reading matters more when it's by a more informed citizen.</li>
      </ul>
      <div>
        The score has a recency bias. The impact from a reading session goes
        down over time. If a Level 10 user read an article for 10 minutes today,
        the article's score would go up by 100 points. Tomorrow, that reading
        session would be worth 100 * (1/2) or 50 points. The next day, it will
        be worth 100 * (1/3) or 33 points.
      </div>
    </div>
  );
};
